// 스타일 불러오기
import '../css/styles.css';

// 유틸리티 및 모듈 불러오기
import { initApp } from './utils/app-initializer.js';
import { setupEventListeners } from './utils/event-listeners.js';
import { checkOnlineStatus } from './utils/network-status.js';
import { initTheme } from './utils/theme-manager.js';
import { initFontSize } from './utils/font-size-manager.js';
import { initVersionDisplay } from './components/version-display.js';

// 애플리케이션 전역 상태 관리
window.appNavigation = {
  currentPage: null,
  previousPage: null,
  historyStack: [],
  shortcutClicked: false, // 바로가기 메뉴 클릭 플래그
  internalNavigation: false // 앱 내 이동 여부 플래그
};

// 채팅 UI가 열려있는지 확인하는 함수
function isChatUIOpen() {
  return window.isChatUIOpen === true;
}

// 채팅 UI를 닫는 함수
function closeChatUI() {
  const chatUIContainer = document.getElementById('chat-ui-container');
  const postsContainer = document.getElementById('posts-container');
  
  if (chatUIContainer && postsContainer) {
    chatUIContainer.style.display = 'none';
    postsContainer.style.display = 'block';
    window.isChatUIOpen = false;
    
    // 최근 채팅방에 대한 리스너 해제
    if (window.currentChatRoomId) {
      import('./utils/firebase-config.js').then(module => {
        const { database } = module;
        import('firebase/database').then(({ ref, off }) => {
          off(ref(database, `chatingRooms/${window.currentChatRoomId}/messages`));
          console.log('채팅 리스너가 해제되었습니다.');
        });
      });
    }
    
    // 더미 상태를 다시 추가하여 앱이 종료되지 않도록 합니다
    setTimeout(() => {
      history.pushState({ page: 'home' }, '', window.location.href);
    }, 100);
    
    return true; // 채팅창을 성공적으로 닫았음을 반환
  }
  
  return false; // 채팅창을 닫지 못했음을 반환
}

// 앱을 종료하는 함수
function closeApp() {
  console.log('앱을 종료합니다...');
  window.history.go(-1);
}

// 브라우저 뒤로가기 이벤트 처리
window.addEventListener('popstate', (event) => {
  // 앱 내 네비게이션 상태 확인
  if (window.appNavigation.internalNavigation) {
    // 앱 내 이동인 경우 플래그 초기화하고 종료
    window.appNavigation.internalNavigation = false;
    return;
  }
  
  // 채팅 UI가 열려있는 경우 채팅창을 닫고 이벤트 중단
  if (isChatUIOpen()) {
    event.preventDefault();
    closeChatUI();
    return;
  }
  
  // 테마 전환 이벤트 체크 (테마 전환 시 종료 확인 다이얼로그를 표시하지 않음)
  if (window.themeToggleClicked) {
    window.themeToggleClicked = false;
    // 더미 히스토리 상태 다시 추가
    history.pushState({ page: 'home' }, '', window.location.href);
    return;
  }
  
  // 현재 홈 화면인 경우
  if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
    // 바로가기 메뉴를 통해 이동했다가 돌아온 경우는 확인 메시지 표시하지 않음
    if (window.appNavigation.shortcutClicked) {
      console.log('바로가기 메뉴에서 돌아옴: 종료 확인 없이 진행');
      window.appNavigation.shortcutClicked = false;
      // 더미 히스토리 상태 다시 추가
      history.pushState({ page: 'home' }, '', window.location.href);
      return;
    }
    
    // 홈 화면에서 뒤로가기 시 앱 종료 확인
    event.preventDefault();
    const confirmExit = confirm('앱을 종료하시겠습니까?');
    
    if (!confirmExit) {
      // 사용자가 종료를 취소한 경우 더미 히스토리 상태 다시 추가
      history.pushState({ page: 'home' }, '', window.location.href);
    } else {
      // 확인을 선택한 경우 앱 종료
      closeApp();
    }
  }
});

// 모바일 환경에서 뒤로가기 버튼 제어를 위한 추가 처리
// Android 기기의 하드웨어 뒤로가기 버튼 처리
document.addEventListener('backbutton', (event) => {
  console.log('모바일 뒤로가기 버튼 감지');
  
  // 채팅 UI가 열려있으면 채팅창을 닫음
  if (isChatUIOpen()) {
    event.preventDefault();
    closeChatUI();
    return false; // 이벤트 전파 방지
  }
  
  // 현재 홈 화면인 경우
  if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
    // 바로가기 메뉴를 통해 이동했다가 돌아온 경우는 확인 메시지 표시하지 않음
    if (window.appNavigation.shortcutClicked) {
      window.appNavigation.shortcutClicked = false; // 플래그 초기화
      console.log('바로가기 메뉴에서 돌아옴: 종료 확인 없이 진행');
      return false; // 이벤트 전파 방지
    }
    
    // 홈 화면에서 뒤로가기 시 앱 종료 확인
    event.preventDefault();
    const confirmExit = confirm('앱을 종료하시겠습니까?');
    
    if (!confirmExit) {
      return false; // 이벤트 전파 방지
    } else {
      // 확인을 선택한 경우 앱 종료
      closeApp();
    }
  }
  
  return true;
}, false);

// 앱 내에서 뒤로가기 버튼 처리를 위한 전역 함수
window.handleBackButtonPress = () => {
  if (isChatUIOpen()) {
    return closeChatUI();
  }
  return false;
};

// 앱 초기화
document.addEventListener('DOMContentLoaded', async () => {
  console.log('SNS PWA 애플리케이션이 시작되었습니다.');
  
  // 온라인 상태 확인 및 모니터링
  checkOnlineStatus();
  
  // 테마 초기화
  initTheme();
  
  // 글씨 크기 초기화
  initFontSize();
  
  // Firebase 및 앱 초기화
  await initApp();
  
  // 이벤트 리스너 설정
  setupEventListeners();
  
  // 더미 히스토리 상태 추가
  history.pushState({ page: 'home' }, '', window.location.href);
  
  // 채팅 UI 상태 초기화
  window.isChatUIOpen = false;
  
  // 테마 전환 플래그 초기화
  window.themeToggleClicked = false;
  
  // 전역 키보드 이벤트 추가 - ESC 키로 채팅창 닫기
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isChatUIOpen()) {
      window.handleBackButtonPress();
    }
  });
  
  // 바로가기 메뉴 클릭 플래그 초기화
  window.appNavigation.shortcutClicked = false;
  
  // 버전 정보 표시 초기화
  initVersionDisplay();
}); 